import { Familia, TipoDomicilioEnum, TipoImovelEnum } from 'graphql/types.generated'
import { ReactNode } from 'react'
import { isUndefinedOrNull } from 'util/checks'
import { applyMask, masks } from 'util/mask'
import { isEmptyOrHyphen } from 'util/string/string-emptyOrHyphen'
import { AnimaisImovelFormModel } from 'view/cadastro-imovel/model-cadastroImovel'
import { EnderecoNaoInformadoText } from 'view/visualizacao-imovel/components/EnderecoNaoInformadoText'
import { CidadaoTerritorioBasicoWithProntuarioId } from 'view/visualizacao-imovel/model-visualizacao-imovel'

import { domicilioTipoImovelRecord, TipoImovelListModel, tipoImovelRecord } from '../model'

export const getTipoImovelIcon = (
  tipoImovel: TipoImovelEnum | undefined | null,
  tipoDomicilioId: TipoDomicilioEnum | undefined | null
): TipoImovelListModel => {
  if (!isUndefinedOrNull(tipoImovel)) {
    if (tipoImovel !== TipoImovelEnum.DOMICILIO) {
      return tipoImovelRecord[tipoImovel]
    }
    if (!isUndefinedOrNull(tipoDomicilioId)) {
      return domicilioTipoImovelRecord[tipoDomicilioId]
    } else {
      return domicilioTipoImovelRecord[TipoDomicilioEnum.CASA]
    }
  }
  return tipoImovelRecord[TipoImovelEnum.OUTROS]
}

export const getResponsavelDependentesFamilia = (
  familia: Pick<Familia, 'cpfCnsResponsavel'> & {
    cidadaosTerritorioBasico: ReadonlyArray<CidadaoTerritorioBasicoWithProntuarioId>
  }
) => {
  return familia.cidadaosTerritorioBasico.reduce<{
    responsavel: CidadaoTerritorioBasicoWithProntuarioId | null
    dependentes: CidadaoTerritorioBasicoWithProntuarioId[]
  }>(
    (acc, cidadao) => {
      if (
        (cidadao.cpf && cidadao.cpf === familia.cpfCnsResponsavel) ||
        (cidadao.cns && cidadao.cns === familia.cpfCnsResponsavel)
      ) {
        acc.responsavel = cidadao
      } else {
        acc.dependentes.push(cidadao)
      }

      return acc
    },
    { responsavel: null, dependentes: [] }
  )
}

export const getTiposAnimaisStringListCapitalized = (animaisImovel: AnimaisImovelFormModel) => {
  const list = [
    animaisImovel.possuiCachorro ? 'Cachorro' : null,
    animaisImovel.possuiGato ? 'Gato' : null,
    animaisImovel.possuiPassaro ? 'Pássaro' : null,
    animaisImovel.possuiGalinha ? 'Galinha' : null,
    animaisImovel.possuiPorco ? 'Porco' : null,
    animaisImovel.possuiMacaco ? 'Macaco' : null,
    animaisImovel.possuiRepteis ? 'Répteis' : null,
    animaisImovel.possuiOutros ? 'Outros' : null,
  ].filterNotNull()

  return list?.isNotEmpty() ? list : null
}

export function getImovelEnderecoFormatado({
  logradouro,
  nomeTipoLogradouro,
  numero,
  complemento,
  nomeInstituicaoPermanencia,
}: {
  logradouro: string | undefined
  nomeTipoLogradouro: string | undefined
  numero: string | undefined
  complemento: string | undefined
  nomeInstituicaoPermanencia: string | undefined
}): ReactNode {
  const isLogradouroNaoInformado = isEmptyOrHyphen(logradouro)
  const prefixoTipoLogradouro = nomeTipoLogradouro ? `${nomeTipoLogradouro.titleCase()} ` : ''

  const logradouroFormatado = isLogradouroNaoInformado ? (
    <EnderecoNaoInformadoText text='Logradouro não informado' />
  ) : (
    `${prefixoTipoLogradouro}${logradouro?.titleCase()}`
  )

  const numeroFormatado = numero ? `nº ${numero}` : 'S/N'

  return (
    <>
      {logradouroFormatado}
      {`, ${numeroFormatado}`}
      {complemento && ` | ${complemento.titleCase()}`}
      {nomeInstituicaoPermanencia && ` | ${nomeInstituicaoPermanencia.titleCase()}`}
    </>
  )
}

export function getImovelCepFormatado(cep: string | undefined | null, addPrefix?: boolean): ReactNode {
  const isCepNaoInformado = isEmptyOrHyphen(cep) || !cep
  const cepMascarado = !isCepNaoInformado ? applyMask(cep, masks.cep) : null

  if (!addPrefix) {
    return cepMascarado
  }

  if (isCepNaoInformado) {
    return (
      <>
        {' - CEP: '}
        <EnderecoNaoInformadoText text='não informado' />
      </>
    )
  }

  return ` - ${cepMascarado}`
}

export function getBairroFormatado(bairro: string | undefined | null) {
  const isBairroNaoInformado = isEmptyOrHyphen(bairro)

  return isBairroNaoInformado ? <EnderecoNaoInformadoText text='bairro não informado' /> : bairro?.titleCase()
}

export function renderizarNumeroMoradores(
  isFichaNaoProcessadaAplicativo: boolean,
  isResponsavelTecnico: boolean,
  numeroMoradoresFicha: number | undefined,
  numeroTotalCidadaos: number
) {
  if (isFichaNaoProcessadaAplicativo && isResponsavelTecnico) return null

  return isResponsavelTecnico ? numeroMoradoresFicha : numeroTotalCidadaos
}
